/**
 * One moderation check (explicit content, contact information or photo subject) on the
 * Photo Review page: its result, its confidence and what the check found.
 */
import { CheckCircle, MinusCircle, XCircle } from 'lucide-react';
import { categoryText, photoSubjectCategoryOf } from './photo-subject-categories';

export interface ModerationCheck {
  log_id: string;
  check_type: 'explicit_content' | 'contact_info' | 'photo_subject';
  result: 'pass' | 'fail' | 'error';
  confidence: number | null;
  details: Record<string, unknown>;
  admin_notes: string | null;
}

const checkTypeLabels: Record<string, string> = {
  explicit_content: 'Explicit Content',
  contact_info: 'Contact Information',
  photo_subject: 'Photo Subject',
};

// The category texts moved to ./photo-subject-categories (2026-10-22): the queue's
// "Flagged For" column shows the same category, and one list keeps them equal.

export function ModerationCheckCard({ check }: { check: ModerationCheck }) {
  // The card background is light in BOTH themes, so the card sets its own text color: an
  // inherited dark-theme foreground was white on light green (PREDEV click test 2026-10-14).
  const failed = check.result === 'fail';
  const findingClass = failed ? 'text-red-700' : undefined;
  // Only a failed check is red: a passed explicit check still lists benign labels (swimwear).
  const chipClass = `inline-flex rounded-full px-2 py-0.5 text-xs ${failed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`;
  return (
    <div
      className={`rounded-lg p-3 ${
        check.result === 'fail'
          ? 'bg-red-50 border border-red-200 text-red-900'
          : check.result === 'error'
            ? 'bg-muted border border-border text-foreground'
            : 'bg-green-50 border border-green-200 text-green-900'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium">
          {checkTypeLabels[check.check_type] || check.check_type}
        </span>
        {/* 'error' = the check did not run (a service fault, the screen failed open). */}
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
          check.result === 'fail'
            ? 'bg-red-100 text-red-800'
            : check.result === 'error'
              ? 'bg-muted text-muted-foreground'
              : 'bg-green-100 text-green-800'
        }`}>
          {check.result === 'fail' ? <XCircle className="h-3 w-3" /> : check.result === 'error' ? <MinusCircle className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
          {check.result === 'fail' ? 'Failed' : check.result === 'error' ? 'Error' : 'Passed'}
        </span>
      </div>
      {(() => {
        // Defense-in-depth: the backend now coerces confidence to
        // a JS number (it's a Postgres `numeric` → serialized as a
        // STRING by node-postgres), but coerce again here so a
        // string can never crash the render via `.toFixed`
        // (the bug this panel hit: "toFixed is not a function").
        const conf = Number(check.confidence);
        return Number.isFinite(conf) && conf > 0 ? (
          <div className="text-xs opacity-80">
            Confidence: {conf.toFixed(1)}%
          </div>
        ) : null;
      })()}
      {check.details && (
        <div className="mt-2 text-xs">
          {check.check_type === 'contact_info' && !!(check.details as Record<string, unknown>).reason && (
            <p className={findingClass}>{String((check.details as Record<string, unknown>).reason)}</p>
          )}
          {check.check_type === 'contact_info' &&
            Array.isArray((check.details as Record<string, unknown>).detected_items) &&
            ((check.details as Record<string, unknown>).detected_items as string[]).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {((check.details as Record<string, unknown>).detected_items as string[]).map((item, i) => (
                <span key={i} className={chipClass}>
                  {item}
                </span>
              ))}
            </div>
          )}
          {check.check_type === 'photo_subject' && (
            <div className="space-y-1">
              {(() => {
                const text = categoryText(photoSubjectCategoryOf(check.details));
                return text && (
                  <p className={failed ? 'font-medium text-red-700' : 'font-medium'} title={text.meaning}>
                    {text.label}
                  </p>
                );
              })()}
              {!!(check.details as Record<string, unknown>).reason && (
                <p className={findingClass}>{String((check.details as Record<string, unknown>).reason)}</p>
              )}
              {Array.isArray((check.details as Record<string, unknown>).detected_items) &&
                ((check.details as Record<string, unknown>).detected_items as string[]).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {((check.details as Record<string, unknown>).detected_items as string[]).map((item, i) => (
                    <span key={i} className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800">
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {check.check_type === 'explicit_content' &&
            Array.isArray((check.details as Record<string, unknown>).labels) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {((check.details as Record<string, unknown>).labels as Array<{ Name?: string; Confidence?: number }>)
                .filter((l) => Number(l.Confidence ?? 0) > 50)
                .map((l, i) => (
                <span key={i} className={chipClass}>
                  {l.Name} ({Number(l.Confidence ?? 0).toFixed(0)}%)
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
